import React from 'react';
import { Button, Space } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../../hooks/useTranslation';

export interface PendingQuickActionsProps {
  appointment: {
    id: string;
    status: string;
    provider_id?: string | null;
    patient_phone?: string | null;
    app_user?: {
      phone_number?: string | null;
    } | null;
    patient?: {
      phone_number?: string | null;
    } | null;
  };
  loadingStates: {
    updating?: boolean;
    cancelling?: boolean;
    markingNoShow?: boolean;
    assigningProvider?: boolean;
  };
  handlers: {
    onMarkConsulted: () => void;
    onCancelAppointment: () => void;
    onMarkNoShow?: () => void;
    onAssignProvider?: () => void;
    onContactPatient?: (phoneNumber: string) => void;
  };
  showNoShowButton?: boolean;
  showAssignProviderButton?: boolean;
  showContactPatientButton?: boolean;
}

const PendingQuickActions: React.FC<PendingQuickActionsProps> = ({
  appointment,
  loadingStates,
  handlers,
  showNoShowButton = false,
  showAssignProviderButton = true,
  showContactPatientButton = true
}) => {
  const { t } = useTranslation();

  // Show assign provider button only when provider_id is empty and status is pending
  const shouldShowAssignProvider = showAssignProviderButton && 
    !appointment.provider_id && 
    appointment.status === 'pending' && 
    handlers.onAssignProvider;

  // Get the best available phone number for contacting the patient
  const getPatientPhoneNumber = (): string | null => {
    return appointment.patient_phone || 
           appointment.app_user?.phone_number || 
           appointment.patient?.phone_number || 
           null;
  };

  const patientPhoneNumber = getPatientPhoneNumber();
  const shouldShowContactPatient = showContactPatientButton && 
    patientPhoneNumber && 
    handlers.onContactPatient;

  const handleContactPatient = () => {
    if (patientPhoneNumber && handlers.onContactPatient) {
      handlers.onContactPatient(patientPhoneNumber);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {/* Assign Provider Button (Only show when no provider assigned) */}
      {shouldShowAssignProvider && (
        <Button
          type="primary"
          icon={<UserOutlined />}
          block
          onClick={handlers.onAssignProvider}
          loading={loadingStates.assigningProvider}
        >
          {t('appointments.assignProvider') || 'Assign Provider'}
        </Button>
      )}

      {/* Contact Patient Button (Only show when phone number is available) */}
      {shouldShowContactPatient && (
        <Button
          icon={<MessageOutlined />}
          block
          onClick={handleContactPatient}
        >
          {t('appointments.contactPatient') || 'Contact Patient (WhatsApp)'}
        </Button>
      )}

      {/* Mark No-Show Button (Optional) */}
      {showNoShowButton && handlers.onMarkNoShow && (
        <Button 
          icon={<ExclamationCircleOutlined />} 
          block
          onClick={handlers.onMarkNoShow}
          loading={loadingStates.markingNoShow}
        >
          {t('appointments.markNoShow') || 'Mark No-Show'}
        </Button>
      )}

      {/* Cancel Appointment Button */}
      <Button
        danger
        icon={<CloseCircleOutlined />}
        block
        onClick={handlers.onCancelAppointment}
        loading={loadingStates.cancelling}
      >
        {t('appointments.cancel')}
      </Button>
    </Space>
  );
};

export default PendingQuickActions;
